// Roadmap Card Component
// Date: January 17, 2025
// Purpose: Card view for Roadmap entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapIcon,
  CalendarIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface RoadmapCardProps {
  roadmap: {
    id: string;
    name: string;
    description?: string;
    status?: string;
    functional_area?: string;
    complexity_level?: number;
    estimated_duration_months?: number;
    estimated_cost_range_min?: number;
    estimated_cost_range_max?: number;
    transformation_count?: number;
    completed_transformations?: number;
    created_at?: string;
    [key: string]: any;
  };
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onCopy?: (item: any) => void;
  onClick?: (id: string) => void;
  canEdit?: (item: any) => boolean;
  customActions?: Array<{
    label: string | ((item: any) => string);
    icon: React.ComponentType<any> | ((item: any) => React.ComponentType<any>);
    onClick: (item: any) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: any) => 'primary' | 'secondary' | 'danger');
  }>;
}

export const RoadmapCard: React.FC<RoadmapCardProps> = ({
  roadmap,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onClick,
  canEdit,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleClick = () => {
    if (onClick) {
      onClick(roadmap.id);
    } else if (onView) {
      onView(roadmap);
    } else {
      navigate(`/tenant/roadmaps/builder?roadmap=${roadmap.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      case 'DRAFT':
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
      case 'ARCHIVED':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'COMPLETED':
        return 'bg-[var(--color-info)]/20 text-[var(--color-info)] border-[var(--color-info)]/30';
      case 'INACTIVE':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)] border-[var(--color-error)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getComplexityColor = (level?: number) => {
    const complexity = level || 1;
    switch (complexity) {
      case 1: return 'bg-[var(--color-success)]/20 text-[var(--color-success)]';
      case 2: return 'bg-[var(--color-info)]/20 text-[var(--color-info)]';
      case 3: return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]';
      case 4: return 'bg-[var(--color-warning)]/30 text-[var(--color-warning)]';
      case 5: return 'bg-[var(--color-error)]/20 text-[var(--color-error)]';
      default: return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  const formatDuration = (months?: number) => {
    if (!months) return 'N/A';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years > 0) {
      return remainingMonths > 0 ? `${years}y ${remainingMonths}m` : `${years}y`;
    } else {
      return `${months}m`;
    }
  };

  const totalProjects = roadmap.transformation_count || 0;
  const completedProjects = roadmap.completed_transformations || 0;
  const activeProjects = totalProjects - completedProjects;
  const completionPercentage = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;

  return (
    <Card
      variant="outline"
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Header - Icon and Actions Row */}
        <div className="flex items-start justify-between mb-2 gap-2">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center">
              <MapIcon className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink min-w-0 flex-wrap justify-end" style={{ maxWidth: 'calc(100% - 2.5rem)' }}>
            {roadmap.status && (
              <Badge className={`text-xs border ${getStatusColor(roadmap.status)} flex-shrink-0`}>
                {roadmap.status}
              </Badge>
            )}
            {/* Action Buttons */}
            <div className="flex items-center gap-1 ml-2 flex-wrap" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '100%' }}>
              {onView && (
                <button
                  onClick={(e) => handleActionClick(e, () => onView(roadmap))}
                  className="p-1.5 text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 rounded-md transition-colors flex-shrink-0"
                  title={t('common.view') || 'View'}
                  type="button"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
              )}
              {onEdit && (canEdit ? canEdit(roadmap) : true) && (
                <button
                  onClick={(e) => handleActionClick(e, () => onEdit(roadmap))}
                  className="p-1.5 text-[var(--color-success)] hover:bg-[var(--color-success)]/20 rounded-md transition-colors flex-shrink-0"
                  title={t('common.edit') || 'Edit'}
                  type="button"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {onCopy && (
                <button
                  onClick={(e) => handleActionClick(e, () => onCopy(roadmap))}
                  className="p-1.5 text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20 rounded-md transition-colors flex-shrink-0"
                  title={t('common.copy') || 'Copy'}
                  type="button"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => handleActionClick(e, () => onDelete(roadmap))}
                  className="p-1.5 text-[var(--color-error)] hover:bg-[var(--color-error)]/20 rounded-md transition-colors flex-shrink-0"
                  title={t('common.delete') || 'Delete'}
                  type="button"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
              {customActions.map((action, index) => {
                const IconComponent = typeof action.icon === 'function' && action.icon.length > 0
                  ? (action.icon as (item: any) => React.ComponentType<any>)(roadmap)
                  : action.icon as React.ComponentType<any>;
                const variant = typeof action.variant === 'function'
                  ? action.variant(roadmap)
                  : action.variant || 'primary';
                const colorClass = variant === 'danger'
                  ? 'text-[var(--color-error)] hover:bg-[var(--color-error)]/20'
                  : variant === 'secondary'
                  ? 'text-[var(--color-secondary-text)] hover:bg-[var(--color-secondary-bg)]'
                  : 'text-[var(--color-accent-bg)] hover:bg-[var(--color-accent-bg)]/20';

                return (
                  <button
                    key={index}
                    onClick={(e) => handleActionClick(e, () => action.onClick(roadmap))}
                    className={`p-1.5 ${colorClass} rounded-md transition-colors flex-shrink-0`}
                    title={typeof action.label === 'function' ? action.label(roadmap) : action.label}
                    type="button"
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Title and Description - Full Width */}
        <div className="w-full mb-3">
          <h3 className="font-semibold text-[var(--color-secondary-text)] mb-2 break-words">
            {roadmap.name}
          </h3>
          {roadmap.description && (
            <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-3 break-words">
              {roadmap.description}
            </p>
          )}
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 gap-3 mb-3 flex-1">
          {/* Projects */}
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-4 h-4 text-[var(--color-accent-text)] opacity-75" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[var(--color-secondary-text)] opacity-75">Projects</div>
              <div className="text-sm font-semibold text-[var(--color-surface-text)]">
                {totalProjects} total
              </div>
              {totalProjects > 0 && (
                <div className="text-xs text-[var(--color-secondary-text)] opacity-75">
                  <span className="text-[var(--color-success)]">{completedProjects}✓</span>
                  <span className="mx-1">•</span>
                  <span className="text-[var(--color-info)]">{activeProjects}●</span>
                </div>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-[var(--color-accent-text)] opacity-75" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[var(--color-secondary-text)] opacity-75">Duration</div>
              <div className="text-sm font-semibold text-[var(--color-surface-text)]">
                {formatDuration(roadmap.estimated_duration_months)}
              </div>
            </div>
          </div>

          {/* Budget */}
          {roadmap.estimated_cost_range_min && (
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="w-4 h-4 text-[var(--color-accent-text)] opacity-75" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--color-secondary-text)] opacity-75">Budget</div>
                <div className="text-sm font-semibold text-[var(--color-surface-text)]">
                  {formatCurrency(roadmap.estimated_cost_range_min)}
                  {roadmap.estimated_cost_range_max && ` - ${formatCurrency(roadmap.estimated_cost_range_max)}`}
                </div>
              </div>
            </div>
          )}

          {/* Complexity */}
          {roadmap.complexity_level && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <div className="flex space-x-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div
                      key={star}
                      className={`w-1 h-1 rounded-full ${star <= roadmap.complexity_level! ? 'bg-current' : 'bg-[var(--color-border)]'}`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--color-secondary-text)] opacity-75">Complexity</div>
                <Badge className={`text-xs ${getComplexityColor(roadmap.complexity_level)}`}>
                  Level {roadmap.complexity_level}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {totalProjects > 0 && (
          <div className="mt-auto">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-[var(--color-secondary-text)] opacity-75">Progress</span>
              <span className="text-xs font-semibold text-[var(--color-surface-text)]">
                {completionPercentage}%
              </span>
            </div>
            <div className="w-full bg-[var(--color-border)] rounded-full h-2">
              <div
                className="bg-[var(--color-accent-bg)] h-2 rounded-full transition-all"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between text-xs">
          {roadmap.functional_area && (
            <span className="text-[var(--color-secondary-text)] opacity-75 truncate">
              {roadmap.functional_area}
            </span>
          )}
          {roadmap.created_at && (
            <span className="text-[var(--color-secondary-text)] opacity-75">
              {new Date(roadmap.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

